// src/app/page.tsx

'use client';

import React, { useEffect, useState } from 'react';

// Components
import Header from '@/components/header/Header';
import Center from '@/components/center/Center';
import Footer from '@/components/footer/Footer';
import Loader from '@/components/loader/Loader';
import AIDoorBackground from '@/components/ai-door-background/AIDoorBackground';
import DoorComponent from '@/components/ai-door-background/DoorComponent';
import GlassMorphicModal from '@/components/modal/GlassMorphicModal';

// Context
import { DoorProvider, useDoorContext } from '@/context/DoorContext';

// Hooks
import useMixpanelTracking from '@/hooks/useMixpanelTracking';
import useTrackPageLoad from '@/hooks/use-track-page-load';
import useDOMContentLoaded from '@/hooks/use-dom-content-loaded';
import useDoorState from '@/hooks/use-door-state';

// Utilities
import { getIp } from '@/utils/getIp';

// Styles
import styles from './page.module.scss';

const PageContent: React.FC = () => {
  const [, setDomContentLoaded] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const { isKillAIPressed, isKillAIReleased, isDoorOpen } = useDoorContext();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [userIp, setUserIp] = useState<string | null>(null);
  const { track, updateProfile } = useMixpanelTracking();

  // Retrieve user IP on component mount
  useEffect(() => {
    const getUserIp = async () => {
      const ip = await getIp();
      setUserIp(ip);
    };
    void getUserIp();
  }, []);

  // Track page load with user IP, tracking, and profile updates
  useTrackPageLoad(userIp, track, updateProfile);
  // Set state when DOM content is loaded
  useDOMContentLoaded(() => setDomContentLoaded(true));
  // Monitor door state changes to control modal visibility
  useDoorState(setIsModalVisible);

  // Hide loader once the animation is complete
  const handleAnimationComplete = () => {
    setShowLoader(false);
  };

  return (
    <div className={styles['page__container']}>
      {/* Loader Section */}
      {showLoader && <Loader onAnimationComplete={handleAnimationComplete} />}

      {/* AI Door Background - Only visible when door is open */}
      {isDoorOpen && (
        <AIDoorBackground className={styles['page__door--open']} />
      )}

      {/* Main Content */}
      <main className={styles['page__main']}>
        <Header />
        <Center setIsModalVisible={setIsModalVisible} />
        <Footer />
      </main>

      {/* AI Door Component */}
      <DoorComponent
        isKillAIPressed={isKillAIPressed}
        isKillAIReleased={isKillAIReleased}
      />

      {/* Modal for additional interactions */}
      <GlassMorphicModal
        isVisible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
      />
    </div>
  );
};

const Page: React.FC = () => {
  return (
    // Provide door context to the entire page content
    <DoorProvider>
      <PageContent />
    </DoorProvider>
  );
};

export default Page;
